import { MutationCache, QueryCache, QueryClient } from '@tanstack/react-query'

import { type ApiError, ClientErrorCode, isApiError } from '@/services/http'

import { notifyError } from './errorHandling'

const MAX_QUERY_RETRIES = 2
const HTTP_STATUS_SERVER_ERROR = 500

function isRetryableError(error: ApiError): boolean {
  const isTransientClientError =
    error.errorCode === ClientErrorCode.NetworkError || error.errorCode === ClientErrorCode.Timeout
  const isServerError = (error.httpStatus ?? 0) >= HTTP_STATUS_SERVER_ERROR
  return isTransientClientError || isServerError
}

export const queryClient = new QueryClient({
  queryCache: new QueryCache({
    onError: (error, query) => {
      if (query.meta?.skipGlobalErrorToast) {
        return
      }
      notifyError(error)
    },
  }),
  mutationCache: new MutationCache({
    onError: (error, _variables, _onMutateResult, mutation) => {
      if (mutation.meta?.skipGlobalErrorToast) {
        return
      }
      notifyError(error)
    },
  }),
  defaultOptions: {
    queries: {
      staleTime: 30_000,
      refetchOnWindowFocus: false,
      retry: (failureCount, error) => {
        if (isApiError(error) && !isRetryableError(error)) {
          return false
        }
        return failureCount < MAX_QUERY_RETRIES
      },
    },
    mutations: {
      // Writes are not idempotent on the backend; never replay them automatically.
      retry: false,
    },
  },
})
